import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { InstalledPlugin } from '@crowdex/plugin-contracts';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { Switch } from '@crowdex/ui/components/switch';
import { Check, CircleDashed } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { useRouter } from '@/lib/navigation';
import { MODELS_PATH } from '@/lib/settings-page/sections';

type Listing = {
  contracts: { contract: string; providers: { id: string; title: string; chosen: boolean }[] }[];
};

/**
 * What each contract a plugin provides is used for (R9 "Used for"). Models and voices are chosen
 * per model in Settings › Models and Voice; code forges are additive (each on or off, every one
 * on serving its hosts); the other roles have one default provider.
 */
export const CONTRACT_USES: Record<
  string,
  { title: string; settings?: { label: string; path: string }; additive?: boolean }
> = {
  'forge.v1': { title: 'Code forge', additive: true },
  'files.v1': { title: 'Files' },
  'rooms.v1': { title: 'Call rooms' },
  'inference.v1': { title: 'Models', settings: { label: 'Models', path: MODELS_PATH } },
  'realtime.v1': {
    title: 'Voice calls',
    settings: { label: 'Voice', path: '/settings?section=voice' },
  },
};

const message = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

/**
 * One row per contract the plugin provides: On for an additive role (code forges), Default where
 * the role has one provider.
 */
export function PluginUsedFor({ plugin, busy }: { plugin: InstalledPlugin; busy: boolean }) {
  const router = useRouter();
  const provides = plugin.crowdex?.provides ?? [];
  const [listing, setListing] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    () =>
      apiFetch<Listing>('/api/providers').then(setListing, (reason: unknown) =>
        setError(message(reason)),
      ),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const chosen = (contract: string) =>
    listing?.contracts
      .find((entry) => entry.contract === contract)
      ?.providers.find((provider) => provider.id === plugin.name)?.chosen === true;
  const choose = (contract: string, on: boolean) =>
    void (
      CONTRACT_USES[contract]?.additive
        ? apiFetch('/api/source-control/forges', {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ plugin: plugin.name, on }),
          })
        : apiFetch(`/api/providers/${encodeURIComponent(contract)}`, {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ plugin: on ? plugin.name : null }),
          })
    ).then(load, (reason: unknown) => setError(message(reason)));
  return (
    <div className="space-y-2">
      {error !== null && <InlineMessage tone="error">{error}</InlineMessage>}
      <div className="overflow-hidden rounded-2xl border bg-card">
        {provides.map(({ contract, title }) => {
          const use = CONTRACT_USES[contract];
          const settings = use?.settings;
          return (
            <div
              className="flex items-center gap-3 border-hairline border-b px-4 py-3 text-sm last:border-b-0"
              data-contract={contract}
              key={contract}
            >
              <span className="min-w-0 flex-1">
                {use?.title ?? title}
                {use === undefined && <span className="text-muted-foreground"> · {contract}</span>}
              </span>
              {settings !== undefined ? (
                <button
                  className={settingButtonClass()}
                  onClick={() => router.push(settings.path)}
                  type="button"
                >
                  {settings.label}
                </button>
              ) : (
                <span className="flex items-center gap-2 text-muted-foreground text-xs">
                  {use?.additive ? 'On' : 'Default'}
                  <Switch
                    aria-label={`${use?.additive ? 'Use' : 'Default'} for ${use?.title ?? title}`}
                    checked={chosen(contract)}
                    disabled={busy || listing === null || !plugin.enabled}
                    onCheckedChange={(on) => choose(contract, on)}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Diagnostics of a plugin built from a `crowdex.json`: whether what it reaches (its MCP servers,
 * the providers it is) has its connection.
 */
export function PluginDiagnostics({
  plugin,
  connected,
}: {
  plugin: InstalledPlugin;
  connected: boolean;
}) {
  const crowdex = plugin.crowdex;
  const rows = [
    ...(crowdex?.mcp ?? []).map((server) => ({
      key: `mcp:${server.id}`,
      label: `MCP server ${server.title}`,
    })),
    ...(crowdex?.provides ?? []).map((provided) => ({
      key: `provides:${provided.contract}`,
      label: `${CONTRACT_USES[provided.contract]?.title ?? provided.contract} provider`,
    })),
  ];
  if (rows.length === 0) return null;
  const ready = plugin.enabled && connected;
  return (
    <ul aria-label="Diagnostics" className="overflow-hidden rounded-2xl border bg-card">
      {rows.map((row) => (
        <li
          className="flex items-center gap-3 border-hairline border-b px-4 py-3 text-sm last:border-b-0"
          key={row.key}
        >
          {ready ? (
            <Check aria-hidden className="size-4 text-success" />
          ) : (
            <CircleDashed aria-hidden className="size-4 text-muted-foreground" />
          )}
          <span className="min-w-0 flex-1">{row.label}</span>
          <span className="text-muted-foreground text-xs">
            {!plugin.enabled ? 'Off' : connected ? 'Connected' : 'Not connected'}
          </span>
        </li>
      ))}
    </ul>
  );
}
